'use client'

import { paths } from '@/config/paths'
import { getTokenImageUrl } from '@/domain/euler'
import type { YieldOpportunity } from '@/features/unified-earn/types'
import { getYieldOpportunityDetailsPath } from '@/features/unified-earn/utils/getYieldOpportunityDetailsPath'
import { EarnPointsBadge } from '@/features/vaults/components/EarnPointsBadge'
import { LinkButton } from '@/ui/atoms/button/Button'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentLayout } from '@/ui/atoms/tooltip/TooltipContentLayout'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SectionHeader } from '@/ui/molecules/section-header/SectionHeader'
import { ArrowRight, Sparkles } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

interface EarnHeroCardProps {
  opportunities: YieldOpportunity[]
}

export function EarnHeroCard({ opportunities }: EarnHeroCardProps) {
  if (opportunities.length === 0) return null
  const showcaseOpportunities = opportunities.slice(0, 4)

  return (
    <div className="pb-7">
      <div className="flex items-end justify-between gap-4">
        <SectionHeader
          icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />}
          title="Earn Vaults"
          description="Managed yield strategies with risk oversight by Clearstar. Select a vault to view assets, current rates, and allocation details."
        />

        <LinkButton
          href={paths.earnVaults}
          variant="outline"
          rounded="full"
          size="sm"
          className="font-bold"
          prefixIcon={<Sparkles className="h-3.5 w-3.5" />}
          postfixIcon={<ArrowRight className="h-3.5 w-3.5" />}
        >
          Vault Benchmarks
        </LinkButton>
      </div>

      <div className="-mx-3 mt-6 sm:mx-0">
        <div className="no-scrollbar flex gap-3 overflow-x-auto px-3 pb-2 sm:grid lg:grid-cols-4 sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0">
          {showcaseOpportunities.map((opportunity, i) => (
            <div
              key={opportunity.id}
              className="w-[280px] shrink-0 sm:w-full"
              style={{
                animationDelay: `${i * 0.08}s`,
                animationName: 'sweep-card-in',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                opacity: 0,
              }}
            >
              <ShowcaseCard opportunity={opportunity} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface ShowcaseCardProps {
  opportunity: YieldOpportunity
}

function ShowcaseCard({ opportunity }: ShowcaseCardProps) {
  const assetSymbol = opportunity.assetSymbol || opportunity.symbol
  const tokenImage = getTokenImageUrl(opportunity.tokenInfo, assetSymbol)
  const href = getYieldOpportunityDetailsPath(opportunity)
  const averageApy = opportunity.supplyApy30d
  const displayApy = averageApy ?? opportunity.supplyAPY
  const hasAverageApy = averageApy != null
  const liveApy = opportunity.supplyAPY

  return (
    <Link
      href={href}
      className="group relative block rounded-xl bg-gradient-to-r from-gradient-yellow/20 to-gradient-green/20 p-[1px] transition-all sm:rounded-2xl hover:from-gradient-yellow/40 hover:to-gradient-green/40"
    >
      <div className="flex h-full min-h-[120px] flex-col justify-between rounded-xl bg-card p-3 sm:min-h-[140px] sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <Image
              src={tokenImage}
              alt={assetSymbol}
              width={32}
              height={32}
              className="h-8 w-8 shrink-0 rounded-full"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold text-base text-white sm:text-2xl">{assetSymbol}</span>
            </div>
          </div>

          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform sm:h-10 sm:w-10 group-hover:scale-110">
            <ArrowRight className="h-4 w-4 text-black sm:h-5 sm:w-5" />
          </div>
        </div>

        <div className="mt-3 flex items-end sm:mt-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex cursor-help items-baseline gap-1">
                <Sparkles className="mb-0.5 h-3 w-3 text-gradient-yellow sm:mb-1 sm:h-4 sm:w-4" />
                <span className="bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-bold text-2xl text-transparent sm:text-4xl">
                  {displayApy !== undefined && displayApy > 0 ? `${displayApy.toFixed(2)}%` : '0.00%'}
                </span>
                <span className="ml-0.5 font-medium text-muted-foreground text-xs sm:ml-1 sm:text-sm">
                  {hasAverageApy ? '30d APY' : 'APY'}
                </span>
                <div className="ml-1">
                  <EarnPointsBadge />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContentLong>
              <TooltipContentLayout>
                <div className="border-border border-b pb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <Typography variant="h4" className="font-medium text-sm leading-none">
                      30d Average APY
                    </Typography>
                    <Typography className="font-bold leading-none">
                      {averageApy !== null && averageApy !== undefined ? `${averageApy.toFixed(2)}%` : 'Unavailable'}
                    </Typography>
                  </div>

                  <TooltipContentLayout.Body>
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      Average supply APY over the past 30 days.
                    </Typography>
                  </TooltipContentLayout.Body>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Typography variant="h4" className="font-medium text-sm leading-none">
                      Live APY
                    </Typography>
                    <Typography className="font-bold leading-none">
                      {liveApy !== undefined ? `${liveApy.toFixed(2)}%` : 'Unavailable'}
                    </Typography>
                  </div>

                  <TooltipContentLayout.Body>
                    <Typography variant="p" className="text-muted-foreground text-xs">
                      Current supply APY based on the latest available rates.
                    </Typography>
                  </TooltipContentLayout.Body>
                </div>
              </TooltipContentLayout>
            </TooltipContentLong>
          </Tooltip>
        </div>
      </div>
    </Link>
  )
}

export function EarnHeroSkeleton() {
  return (
    <div className="rounded-3xl bg-gradient-to-br from-gradient-yellow/10 via-gradient-green/5 to-transparent p-[1px]">
      <div className="rounded-3xl bg-card px-7 py-7 sm:px-10 sm:py-8">
        <div className="flex flex-col gap-2">
          <div className="h-6 w-28 animate-pulse rounded bg-white/5" />
          <div className="h-4 w-80 animate-pulse rounded bg-white/5" />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={`earn-skel-${i}`}
              className="min-h-[120px] rounded-xl bg-white/[0.04] p-3 sm:min-h-[140px] sm:rounded-2xl sm:p-5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="h-7 w-7 animate-pulse rounded-full bg-white/5" />
                  <div className="flex flex-col gap-1.5">
                    <div className="h-4 w-16 animate-pulse rounded bg-white/5" />
                    <div className="h-3 w-20 animate-pulse rounded bg-white/5" />
                  </div>
                </div>
                <div className="h-8 w-8 animate-pulse rounded-full bg-white/5 sm:h-10 sm:w-10" />
              </div>
              <div className="mt-6 h-7 w-24 animate-pulse rounded bg-white/5 sm:mt-8 sm:h-9" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
